import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

// Extensiones/tipos visualizables por el navegador sin convertir el archivo.
const IMG_EXTS = ["png", "jpg", "jpeg", "webp", "svg"];

export function isPreviewableFile(f) {
  const name = (f?.original_filename || f?.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  const ct = (f?.content_type || "").toLowerCase();
  if (ct.startsWith("image/") && !ct.includes("postscript")) return true;
  if (ct === "application/pdf") return true;
  return IMG_EXTS.includes(ext) || ext === "pdf";
}

function isPdfFile(f) {
  const name = (f?.original_filename || f?.name || "").toLowerCase();
  return (f?.content_type || "").toLowerCase() === "application/pdf" || name.endsWith(".pdf");
}

// Visor reutilizable: Perfil del Cliente, Estimate, Invoice, WO y Tareas.
// props: file ({name, original_filename, content_type}), downloadUrl, onClose
export function FilePreviewModal({ file, downloadUrl, onClose }) {
  const { t } = useI18n();
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const printFrameRef = useRef(null);
  const pdf = isPdfFile(file);

  useEffect(() => {
    let url = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(downloadUrl, {
          withCredentials: true,
          responseType: "blob",
        });
        const typed = new Blob([res.data], {
          type: file?.content_type || res.data.type || "application/octet-stream",
        });
        url = URL.createObjectURL(typed);
        if (!cancelled) setBlobUrl(url);
      } catch {
        if (!cancelled) toast.error(t("file_preview_load_err"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url); // liberar memoria al cerrar/cambiar
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadUrl]);

  const doDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = file?.original_filename || file?.name || "archivo";
    a.click();
  };

  const doPrint = () => {
    if (!blobUrl) return;
    if (pdf) {
      // Visor nativo del PDF dentro del iframe → imprime sólo el PDF.
      try {
        printFrameRef.current?.contentWindow?.print();
      } catch {
        window.open(blobUrl, "_blank");
      }
      return;
    }
    // Imagen: iframe oculto con SOLO la imagen original → print() imprime
    // únicamente el archivo, nunca la interfaz de GP.
    const frame = document.createElement("iframe");
    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    document.body.appendChild(frame);
    const doc = frame.contentWindow.document;
    doc.open();
    doc.write(
      `<html><head><title>${(file?.name || "").replace(/</g, "")}</title>` +
        `<style>body{margin:0}img{max-width:100%;max-height:100vh}</style></head>` +
        `<body><img src="${blobUrl}" onload="window.focus();window.print();" /></body></html>`
    );
    doc.close();
    setTimeout(() => document.body.removeChild(frame), 60000);
  };

  return (
    <div
      data-testid="file-preview-overlay"
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        data-testid="file-preview-modal"
        className="bg-white w-full max-w-5xl max-h-[94vh] flex flex-col border border-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-black flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-bold truncate">{file?.name || file?.original_filename}</div>
            <div className="text-xs text-[var(--text-secondary)] truncate">
              {file?.original_filename}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              data-testid="file-preview-print"
              onClick={doPrint}
              disabled={!blobUrl}
              className="font-mono-tech text-xs font-bold border border-black px-3 py-2 hover:bg-black hover:text-white transition-colors disabled:opacity-40"
            >
              {t("file_preview_print")}
            </button>
            <button
              type="button"
              data-testid="file-preview-download"
              onClick={doDownload}
              disabled={!blobUrl}
              className="font-mono-tech text-xs font-bold border border-black px-3 py-2 hover:bg-black hover:text-white transition-colors disabled:opacity-40"
            >
              {t("file_preview_download")}
            </button>
            <button
              type="button"
              data-testid="file-preview-close"
              onClick={onClose}
              className="font-mono-tech text-xs font-bold border border-black px-3 py-2 bg-black text-white hover:bg-white hover:text-black transition-colors"
            >
              {t("file_preview_close")}
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-[60vh] bg-[var(--bg,#f5f5f5)] flex items-center justify-center overflow-auto p-3">
          {loading && (
            <div className="text-sm text-[var(--text-secondary)]">…</div>
          )}
          {!loading && blobUrl && pdf && (
            <iframe
              ref={printFrameRef}
              data-testid="file-preview-pdf"
              title="preview-pdf"
              src={blobUrl}
              className="w-full h-[75vh] border-0 bg-white"
            />
          )}
          {!loading && blobUrl && !pdf && (
            <img
              data-testid="file-preview-image"
              src={blobUrl}
              alt={file?.name || ""}
              className="max-w-full max-h-[78vh] object-contain bg-white"
            />
          )}
        </div>
      </div>
    </div>
  );
}
